import { useState, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import { fetchWithAuth } from '../utils/api'
import { User, Site, Area } from '../types'

interface TaskFormProps {
  user: User
  onSubmit: () => void
  onCancel: () => void
  initialData?: any
  isEditing?: boolean
  taskId?: number
}

interface UserOption {
  id: number
  username: string
  full_name: string
}

// Fix Leaflet marker icon paths
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/images/marker-icon-2x.png',
  iconUrl: '/images/marker-icon.png',
  shadowUrl: '/images/marker-shadow.png',
})

// Location picker component
function LocationPicker({ position, onLocationChange }: { 
  position: [number, number] | null, 
  onLocationChange: (coords: [number, number]) => void 
}) {
  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng
        onLocationChange([lat, lng])
      }
    })
    return null
  }

  return (
    <MapContainer
      center={position || [59.3293, 18.0686]} // Default to Stockholm
      zoom={13}
      style={{ height: '300px', width: '100%' }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />
      <MapClickHandler />
      {position && <Marker position={position} />}
    </MapContainer>
  )
}

function TaskForm({ onSubmit, onCancel, initialData, isEditing = false, taskId }: TaskFormProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'suggested',
    assigned_to: '',
    site_id: '',
    area_id: '',
    estimated_hours: '',
    due_date: '',
    location_coords: null,
    ...initialData
  })
  
  const [sites, setSites] = useState<Site[]>([])
  const [areas, setAreas] = useState<Area[]>([])
  const [users, setUsers] = useState<UserOption[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showLocationMap, setShowLocationMap] = useState(false)

  useEffect(() => {
    fetchSites()
    fetchUsers()
  }, [])

  useEffect(() => {
    if (formData.site_id) {
      fetchAreas(parseInt(formData.site_id))
    } else {
      setAreas([])
      setFormData((prev: any) => ({ ...prev, area_id: '' }))
    }
  }, [formData.site_id])

  const fetchSites = async () => {
    try {
      const response = await fetchWithAuth('/api/admin/sites')
      if (response.ok) {
        const data = await response.json()
        setSites(data)
      }
    } catch (err) {
      console.error('Failed to fetch sites:', err)
    }
  }

  const fetchAreas = async (siteId: number) => {
    try {
      const response = await fetchWithAuth(`/api/admin/sites/${siteId}/areas`)
      if (response.ok) {
        const data = await response.json()
        setAreas(data)
      }
    } catch (err) {
      console.error('Failed to fetch areas:', err)
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await fetchWithAuth('/api/admin/users')
      if (response.ok) {
        const data = await response.json()
        setUsers(data.filter((u: any) => !u.is_disabled))
      }
    } catch (err) {
      console.error('Failed to fetch users:', err)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    if (!formData.title || !formData.title.trim()) {
      setError('Title is required')
      setLoading(false)
      return
    }

    try {
      const payload = {
        title: formData.title?.trim() || '',
        description: formData.description?.trim() || null,
        priority: formData.priority,
        status: formData.status,
        assigned_to: formData.assigned_to && formData.assigned_to.toString().trim() ? parseInt(formData.assigned_to) : null,
        site_id: formData.site_id ? parseInt(formData.site_id) : null,
        area_id: formData.area_id ? parseInt(formData.area_id) : null,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        due_date: formData.due_date || null,
        location_coords: formData.location_coords
      }

      const url = isEditing ? `/api/tasks/${taskId}` : '/api/tasks'
      const method = isEditing ? 'PATCH' : 'POST'
      
      const response = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || `Failed to ${isEditing ? 'update' : 'create'} task`)
      }

      onSubmit()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${isEditing ? 'update' : 'create'} task`)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: string, value: string) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }))
  }

  return (
    <div className="task-form-overlay">
      <div className="task-form-container compact">
        <div className="task-form-header">
          <h3>📋 {isEditing ? 'Edit Task' : 'Create New Task'}</h3>
          <button className="close-button" onClick={onCancel}>×</button>
        </div>

        {error && <div className="error">{error}</div>}

        <form onSubmit={handleSubmit} className="task-form">
          <div className="form-group">
            <label>Task Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter task title..."
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe the task in detail..."
              rows={2}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            {isEditing && (
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status || 'suggested'}
                  onChange={(e) => handleChange('status', e.target.value)}
                >
                  <option value="suggested">Suggested</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="approved">Approved</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="verified">Verified</option>
                  <option value="on_hold">On Hold</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="needs_revision">Needs Revision</option>
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Estimated Hours</label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={formData.estimated_hours}
                onChange={(e) => handleChange('estimated_hours', e.target.value)}
                placeholder="0.0"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Due Date</label>
              <input
                type="datetime-local"
                value={formData.due_date}
                onChange={(e) => handleChange('due_date', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Assign To</label>
              <select
                value={formData.assigned_to}
                onChange={(e) => handleChange('assigned_to', e.target.value)}
              >
                <option value="">Unassigned</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.full_name} ({u.username})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-section">
            <h4>📍 Location</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Site</label>
                <select
                  value={formData.site_id}
                  onChange={(e) => handleChange('site_id', e.target.value)}
                >
                  <option value="">Select site...</option>
                  {sites.map(site => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Area</label>
                <select
                  value={formData.area_id}
                  onChange={(e) => handleChange('area_id', e.target.value)}
                  disabled={!formData.site_id}
                >
                  <option value="">Select area...</option>
                  {areas.map(area => (
                    <option key={area.id} value={area.id}>
                      {area.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <div className="location-header">
                <label>GPS Location (Optional)</label>
                <button 
                  type="button" 
                  className="btn btn-sm"
                  onClick={() => setShowLocationMap(!showLocationMap)}
                >
                  {showLocationMap ? 'Hide Map' : 'Pick Location on Map'}
                </button>
              </div>
              
              {formData.location_coords && (
                <div className="location-display">
                  📍 Lat: {formData.location_coords[0].toFixed(6)}, Lng: {formData.location_coords[1].toFixed(6)}
                  <button 
                    type="button" 
                    className="clear-location" 
                    onClick={() => setFormData((prev: any) => ({ ...prev, location_coords: null }))}
                  >
                    ×
                  </button>
                </div>
              )}

              {showLocationMap && (
                <div className="map-section">
                  <LocationPicker
                    position={formData.location_coords}
                    onLocationChange={(coords) => 
                      setFormData((prev: any) => ({ ...prev, location_coords: coords }))
                    }
                  />
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-success" disabled={loading}>
              {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? '✓ Update Task' : '✓ Create Task')}
            </button>
            <button type="button" className="btn" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default TaskForm